import { useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { FileText, Layers } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";

import apiClient from "../../../api-client";
import { UnifiedModal } from "../../../components/UnifiedModal";
import { Button } from "../../../components/Button";
import { Checkbox } from "../../../components/Checkbox";
import { CustomSelector } from "../../../components/CustomSelector";
import { CustomTextField } from "../../../components/CustomTextField";
import { Label } from "../../../components/Label";
import { InfoTooltip } from "../../../components/InfoTooltip";
import { AlertNote } from "../../../components/AlertNote";
import { getData } from "../../../components/hooks/getData";
import { useAuth } from "../../../components/contexts/AuthContext";
import { brand } from "../../../theme/theme";
import { withClientAccess } from "../../../utils/clientAccess";
import {
  BuyerPersonasEditor,
  fromBackendPersonas,
  isPersonaValid,
  toBackendPersonas,
} from "../BuyerPersonasEditor";

// Checkboxes that behave as a pair: exactly one is always checked.
const CREATION_MODES = [
  {
    manual: false,
    id: "create-content-ai",
    label: "Con IA",
    description: "La IA redacta el contenido a partir de una temática o propone una campaña.",
  },
  {
    manual: true,
    id: "create-content-manual",
    label: "Manual",
    description: "Escribe o pega el texto de redes sociales y blogs.",
  },
];

const CONTENT_TYPE_OPTIONS = [
  {
    value: "single",
    Icon: FileText,
    title: "Contenido individual",
    description: "Una única pieza a partir de una temática concreta.",
  },
  {
    value: "campaign",
    Icon: Layers,
    title: "Campaña",
    description: "Varias piezas a la vez: la IA propone los temas y tú los revisas.",
  },
];

const configDetailPath = (configId) => `content-generation/configs/${configId}/`;

const errorMessage = (error, fallback) =>
  error.response?.data?.error || error.response?.data?.detail || fallback;

/** Loads a config's personas and tells whether the local copy differs from the saved one. */
function useConfigPersonas(configId) {
  const [personas, setPersonas] = useState([]);
  const [savedPersonas, setSavedPersonas] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setPersonas([]);
    setSavedPersonas([]);
    if (!configId) return;

    let cancelled = false;
    setIsLoading(true);
    apiClient
      .get(configDetailPath(configId))
      .then(({ data }) => {
        if (cancelled) return;
        const loaded = fromBackendPersonas(data.buyer_personas);
        setPersonas(loaded);
        setSavedPersonas(loaded);
      })
      .catch((error) => console.error("Error loading config personas:", error))
      .finally(() => !cancelled && setIsLoading(false));
    return () => {
      cancelled = true;
    };
  }, [configId]);

  const isDirty = useMemo(
    () => JSON.stringify(personas) !== JSON.stringify(savedPersonas),
    [personas, savedPersonas]
  );

  return { personas, setPersonas, isLoading, isDirty };
}

function PersonasSection({ personas, onChange, isLoading }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <Label>Buyer personas</Label>
        {/* Above UnifiedModal (z-index 100000) */}
        <InfoTooltip
          title="Define la audiencia objetivo para personalizar el contenido (se recomiendan entre 2 y 4)."
          size={14}
          zIndex={100001}
        />
      </Box>
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <BuyerPersonasEditor personas={personas} onChange={onChange} />
      )}
    </Box>
  );
}

function OptionCards({ options, selected, onSelect }) {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, gap: 1.5 }}>
      {options.map(({ value, Icon, title, description }) => {
        const isSelected = selected === value;
        return (
          <Box
            key={value}
            component="button"
            type="button"
            aria-pressed={isSelected}
            onClick={() => onSelect(value)}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5,
              p: 2,
              textAlign: "left",
              borderRadius: "12px",
              border: `1px solid ${isSelected ? brand.pink : "#E5E7EB"}`,
              backgroundColor: isSelected ? brand.soft : "#fff",
              font: "inherit",
              cursor: "pointer",
              transition: "border-color 0.15s ease, background-color 0.15s ease",
              "&:hover": { borderColor: brand.softBorder, backgroundColor: brand.soft },
              "&:focus-visible": { outline: `2px solid ${brand.pink}`, outlineOffset: 2 },
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                flexShrink: 0,
                borderRadius: "10px",
                background: brand.gradientDiagonal,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon size={18} color="#fff" />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", color: "#0F172A" }}>
                {title}
              </Typography>
              <Typography sx={{ fontSize: "0.8rem", color: "#6B7280", lineHeight: 1.45 }}>
                {description}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

/**
 * Single-step creation: client + mode, then buyer personas and content type (AI) or just
 * a title (manual, always a single piece). A client without config gets it created here; an existing config is only PATCHed if its personas changed.
 * `configuredClients` items: { id: configId, clientId, name }.
 */
export function CreateContentModal({
  open,
  onClose,
  configuredClients,
  defaultClientId = null,
  agencyFilterId = null,
  onContinue,
  // Tutorial: { client, personas, contentType } shown prefilled and read-only; nothing is fetched or saved.
  tutorialMock = null,
}) {
  const { interceptorReady, user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedClientId, setClientId] = useState(null);
  const [isManual, setIsManual] = useState(false);
  const [selectedContentType, setContentType] = useState(null);
  const clientId = tutorialMock ? tutorialMock.client.clientId : selectedClientId;
  const contentType = tutorialMock ? tutorialMock.contentType : selectedContentType;
  const [title, setTitle] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const withoutConfigPath = `content-generation/configs/clients-without-config/${
    agencyFilterId ? `?agency=${agencyFilterId}` : ""
  }`;
  const { data: clientsWithoutConfig, isLoading: isLoadingClients } = getData(
    open && !tutorialMock ? withoutConfigPath : null,
    interceptorReady && open && !tutorialMock,
    [open]
  );

  const clientOptions = useMemo(() => {
    if (tutorialMock) return [{ value: tutorialMock.client.clientId, label: tutorialMock.client.name }];
    const options = [
      ...configuredClients.map((client) => ({ value: client.clientId, label: client.name })),
      ...(Array.isArray(clientsWithoutConfig) ? clientsWithoutConfig : []).map((client) => ({
        value: client.id,
        label: client.name,
      })),
    ];
    return withClientAccess(options.sort((a, b) => a.label.localeCompare(b.label, "es")), user);
  }, [configuredClients, clientsWithoutConfig, tutorialMock, user]);

  const configId = useMemo(
    () =>
      configuredClients.find((client) => String(client.clientId) === String(clientId))?.id ?? null,
    [configuredClients, clientId]
  );
  const {
    personas: loadedPersonas,
    setPersonas,
    isLoading: isLoadingPersonas,
    isDirty,
  } = useConfigPersonas(tutorialMock ? null : configId);
  const personas = tutorialMock ? tutorialMock.personas : loadedPersonas;

  // Two clients without config share configId=null: personas belong to the selected client
  useEffect(() => {
    if (!configId) setPersonas([]);
  }, [clientId, configId, setPersonas]);

  useEffect(() => {
    if (!open) return;
    setClientId(defaultClientId ?? null);
    setIsManual(false);
    setContentType(null);
    setTitle("");
    setError(null);
  }, [open, defaultClientId]);

  const canSubmit =
    Boolean(clientId) &&
    (isManual ? Boolean(title.trim()) : Boolean(contentType) && personas.every(isPersonaValid)) &&
    !isLoadingPersonas &&
    !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit || tutorialMock) return;
    setIsSubmitting(true);
    setError(null);
    try {
      let targetConfigId = configId;
      if (!targetConfigId) {
        const { data } = await apiClient.post("content-generation/configs/", {
          client: clientId,
          language: "es",
          buyer_personas: toBackendPersonas(isManual ? [] : personas),
        });
        targetConfigId = data.id;
      } else if (isDirty && !isManual) {
        await apiClient.patch(configDetailPath(targetConfigId), {
          buyer_personas: toBackendPersonas(personas),
        });
        queryClient.invalidateQueries({ queryKey: ["getData", configDetailPath(targetConfigId)] });
      }
      onContinue({
        configId: targetConfigId,
        type: isManual ? "manual" : contentType,
        title: title.trim(),
        isNewConfig: !configId,
      });
    } catch (submitError) {
      console.error("Error preparing content creation:", submitError);
      setError(errorMessage(submitError, "No se ha podido guardar el cliente. Inténtalo de nuevo."));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <UnifiedModal
      open={open}
      onClose={onClose}
      title="Crear contenido"
      maxWidth="sm"
      footer={
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1.5 }}>
          {error && (
            <Typography variant="body2" color="error" sx={{ mr: "auto" }}>
              {error}
            </Typography>
          )}
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {isSubmitting && <CircularProgress size={14} sx={{ color: "inherit" }} />}
            Continuar
          </Button>
        </Box>
      }
    >
      <Box data-tutorial="create-content-form" sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Label>
            Cliente <span style={{ color: "red" }}>*</span>
          </Label>
          <CustomSelector
            value={clientOptions.find((option) => String(option.value) === String(clientId))?.value ?? ""}
            onChange={(value) => setClientId(value || null)}
            options={clientOptions}
            searchable
            placeholder={isLoadingClients ? "Cargando clientes..." : "Seleccionar cliente"}
            disabled={isLoadingClients && clientOptions.length === 0}
          />
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Label>
            Modo de creación <span style={{ color: "red" }}>*</span>
          </Label>
          {CREATION_MODES.map(({ manual, id, label, description }) => (
            <Box key={id} sx={{ display: "flex", alignItems: "flex-start", gap: 1, ml: "-4px" }}>
              <Checkbox id={id} checked={isManual === manual} onCheckedChange={() => setIsManual(manual)} />
              <Box>
                <Label htmlFor={id}>{label}</Label>
                <Typography sx={{ fontSize: "0.8rem", color: "#6B7280", lineHeight: 1.45 }}>
                  {description}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        {isManual && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Label htmlFor="create-content-title">
              Título del contenido <span style={{ color: "red" }}>*</span>
            </Label>
            <CustomTextField
              id="create-content-title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  handleSubmit();
                }
              }}
              placeholder="Ej: Promoción de Navidad -40%"
            />
          </Box>
        )}

        {clientId && !isManual && (
          <PersonasSection
            personas={personas}
            onChange={setPersonas}
            isLoading={isLoadingPersonas}
          />
        )}

        {!isManual && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Label>
              Tipo de contenido <span style={{ color: "red" }}>*</span>
            </Label>
            <OptionCards options={CONTENT_TYPE_OPTIONS} selected={contentType} onSelect={setContentType} />
          </Box>
        )}
      </Box>
    </UnifiedModal>
  );
}

/** Edits the buyer personas of an existing config; `target` is { configId, clientName } or null. */
export function EditBuyerPersonasModal({ target, onClose, onSaved }) {
  const queryClient = useQueryClient();
  const configId = target?.configId ?? null;
  const { personas, setPersonas, isLoading, isDirty } = useConfigPersonas(configId);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => setError(null), [configId]);

  const handleSave = async () => {
    if (!isDirty) {
      onClose();
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await apiClient.patch(configDetailPath(configId), {
        buyer_personas: toBackendPersonas(personas),
      });
      queryClient.invalidateQueries({ queryKey: ["getData", configDetailPath(configId)] });
      onSaved?.();
      onClose();
    } catch (saveError) {
      console.error("Error saving buyer personas:", saveError);
      setError(errorMessage(saveError, "No se han podido guardar los buyer personas."));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <UnifiedModal
      open={Boolean(target)}
      onClose={onClose}
      title={`Buyer persona de ${target?.clientName || "cliente"}`}
      maxWidth="sm"
      footer={
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1.5 }}>
          {error && (
            <Typography variant="body2" color="error" sx={{ mr: "auto" }}>
              {error}
            </Typography>
          )}
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={isLoading || isSaving || !personas.every(isPersonaValid)}
          >
            {isSaving && <CircularProgress size={14} sx={{ color: "inherit" }} />}
            Guardar
          </Button>
        </Box>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <AlertNote variant="info">
          Los cambios solo se aplicarán a los contenidos que se generen a partir de ahora. Los ya
          creados no se modifican.
        </AlertNote>
        <PersonasSection personas={personas} onChange={setPersonas} isLoading={isLoading} />
      </Box>
    </UnifiedModal>
  );
}
